import { Icon } from '@iconify-icon/react';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout } from '@/components/modules/Docs';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'what-are-citations', title: '1. What Are Citations?' },
  { id: 'how-citations-work', title: '2. How Citations Work' },
  { id: 'citation-settings', title: '3. Citation Settings' },
  { id: 'improving-quality', title: '4. Improving Citation Quality' },
  { id: 'understanding-scores', title: '5. Understanding Relevance Scores' },
  { id: 'troubleshooting', title: '6. Troubleshooting Poor Citations' },
];

const CitationQualityPage = () => {
  return (
    <>
      <NextSeo
        title="Citation Quality Guide"
        description="Learn how to improve AI response accuracy with better citations, source attribution, and knowledge base optimization."
        canonical="https://chat.co/docs/citation-quality"
        openGraph={{
          url: 'https://chat.co/docs/citation-quality',
          title: 'Citation Quality Guide | Chat.co',
          description:
            'Learn how to improve AI response accuracy with better citations, source attribution, and knowledge base optimization.',
          type: 'article',
          article: {
            publishedTime: '2026-01-01T00:00:00Z',
            modifiedTime: '2026-01-26T00:00:00Z',
            section: 'Troubleshooting',
            tags: ['citations', 'AI accuracy', 'source attribution', 'RAG quality'],
          },
          images: [
            {
              url: 'https://chat.co/images/og-docs.png',
              width: 1200,
              height: 630,
              alt: 'Chat.co - Citation Quality',
            },
          ],
        }}
        twitter={{
          cardType: 'summary_large_image',
        }}
      />

      <DocsArticleLayout
        category="Troubleshooting"
        categoryColor="text-rose-700"
        title="Citation Quality"
        description="Make your AI assistant more trustworthy with accurate source citations."
        readingTime="7 min read"
        lastUpdated="January 2026"
        tableOfContents={tableOfContents}
        prevArticle={{
          title: 'Debugging Guide',
          href: PageLink.docsTroubleshooting,
        }}
        nextArticle={{
          title: 'Security & Compliance',
          href: PageLink.docsSecurityCompliance,
        }}
        keyTakeaways={[
          'Citations show users exactly where answers come from',
          'Enable citations in Appearance settings for transparency',
          'Better document structure = better citation accuracy',
          'Relevance scores indicate how well sources match the query',
          'Missing citations often mean content gaps in your knowledge base',
        ]}>
        {/* Introduction */}
        <p className="lead text-lg text-gray-700">
          Citations are what separate a trustworthy AI assistant from a black box. When your chatbot
          shows users exactly where its answers come from, they can verify the information and trust
          the response. This guide explains how citations work and how to optimize them for
          accuracy.
        </p>

        {/* Section 1: What Are Citations */}
        <section id="what-are-citations" className="scroll-mt-24">
          <h2>1. What Are Citations?</h2>

          <p>
            Citations are references to the source documents that informed the AI&apos;s response.
            When enabled, they appear as footnotes below each answer, showing users exactly which
            documents were used.
          </p>

          <h3>Example Citation Display</h3>
          <div className="my-6 rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-3 border-b border-gray-100 pb-3">
              <p className="text-sm text-gray-700">
                Our return policy allows full refunds within 30 days of purchase. After 30 days, we
                offer store credit for items in original condition.
              </p>
            </div>
            <div className="text-xs text-gray-500">
              <p className="mb-2 font-medium text-gray-600">Footnotes:</p>
              <ol className="list-inside list-decimal space-y-1">
                <li>
                  <span className="inline-flex items-center gap-1">
                    <Icon icon="tabler:file-text" className="text-gray-400" />
                    <span className="text-primary-600">Return-Policy-2024.pdf</span>
                    <span className="text-gray-400">(score: 0.94)</span>
                  </span>
                </li>
                <li>
                  <span className="inline-flex items-center gap-1">
                    <Icon icon="tabler:link" className="text-gray-400" />
                    <span className="text-primary-600">https://company.com/returns</span>
                    <span className="text-gray-400">(score: 0.87)</span>
                  </span>
                </li>
              </ol>
            </div>
          </div>

          <h3>Why Citations Matter</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-green-100">
                <Icon icon="tabler:shield-check" className="text-lg text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Build trust</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Users can verify answers by clicking through to source documents. This
                  transparency builds confidence in your AI assistant.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-blue-100">
                <Icon icon="tabler:bug" className="text-lg text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Catch errors</h4>
                <p className="mt-1 text-sm text-gray-600">
                  If the AI cites the wrong document, you know there&apos;s a problem with your
                  knowledge base or the question was ambiguous.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-purple-100">
                <Icon icon="tabler:bulb" className="text-lg text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Guide improvements</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Relevance scores show which documents are being retrieved. Low scores indicate
                  your knowledge base may need better content.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: How Citations Work */}
        <section id="how-citations-work" className="scroll-mt-24">
          <h2>2. How Citations Work</h2>

          <p>
            When a user asks a question, Chat.co uses a sophisticated retrieval process to find
            relevant information and attribute sources.
          </p>

          <h3>The Retrieval Process</h3>
          <div className="my-6 space-y-4">
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-600">
                1
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Hybrid Search</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Your question is analyzed using both semantic search (understanding meaning) and
                  keyword search (matching exact terms). This hybrid approach finds relevant content
                  even when users phrase questions differently than your documents.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-600">
                2
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Document Retrieval</h4>
                <p className="mt-1 text-sm text-gray-600">
                  The system retrieves 15-30 potentially relevant document chunks from your
                  knowledge base based on the search results.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-600">
                3
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Reranking</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Retrieved documents are reranked using advanced AI models to identify the 5-10
                  most relevant pieces. This step dramatically improves citation accuracy.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-600">
                4
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Response Generation</h4>
                <p className="mt-1 text-sm text-gray-600">
                  The AI generates an answer based on the top-ranked documents, tracking which
                  sources informed each part of the response.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-sm font-bold text-primary-600">
                5
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Citation Display</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Sources are displayed as clickable footnotes with relevance scores. Users can
                  click to view or download the original documents.
                </p>
              </div>
            </div>
          </div>

          <h3>What Gets Cited</h3>
          <ul>
            <li>
              <strong>Uploaded documents</strong> — PDFs, Word docs, text files you&apos;ve added
            </li>
            <li>
              <strong>Crawled web pages</strong> — URLs you&apos;ve imported into the knowledge base
            </li>
            <li>
              <strong>Q&A pairs</strong> — Custom question-answer training data
            </li>
          </ul>
        </section>

        {/* Section 3: Citation Settings */}
        <section id="citation-settings" className="scroll-mt-24">
          <h2>3. Citation Settings</h2>

          <p>You can control whether citations are displayed to users.</p>

          <h3>Enabling/Disabling Citations</h3>
          <ol>
            <li>Go to your chatbot&apos;s dashboard</li>
            <li>
              Click <code>Appearance</code> in the sidebar
            </li>
            <li>
              Find the <strong>Citations</strong> toggle
            </li>
            <li>Enable or disable as needed</li>
            <li>Save changes</li>
          </ol>

          <div className="my-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white">
                <Icon icon="tabler:quote" className="text-xl text-gray-500" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Citations</h4>
                <p className="text-sm text-gray-600">
                  When enabled, answers include footnotes and source references (where available).
                </p>
              </div>
              <div className="ml-auto">
                <div className="relative h-6 w-11 rounded-full bg-primary-500">
                  <div className="absolute right-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow" />
                </div>
              </div>
            </div>
          </div>

          <h3>When to Disable Citations</h3>
          <p>While citations are generally recommended, you might disable them for:</p>
          <ul>
            <li>
              <strong>Simple FAQ bots</strong> — Where the source is obvious
            </li>
            <li>
              <strong>Cleaner UI</strong> — For consumer-facing chatbots where footnotes feel too
              formal
            </li>
            <li>
              <strong>Confidential sources</strong> — When you don&apos;t want to reveal document
              names
            </li>
          </ul>

          <div className="my-6 rounded-lg border-l-4 border-blue-500 bg-blue-50 p-4">
            <p className="text-sm text-blue-900">
              <strong>Recommendation:</strong> Keep citations enabled for support, knowledge base,
              and internal assistant use cases. The transparency helps users trust the information.
            </p>
          </div>
        </section>

        {/* Section 4: Improving Quality */}
        <section id="improving-quality" className="scroll-mt-24">
          <h2>4. Improving Citation Quality</h2>

          <p>
            The quality of your citations depends on the quality of your knowledge base. Here&apos;s
            how to optimize for better source attribution.
          </p>

          <h3>Document Structure</h3>
          <div className="my-6 space-y-3">
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Use clear headings</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Structure documents with H1, H2, H3 headers. This helps the AI understand content
                  hierarchy and retrieve the most relevant sections.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Keep sections focused</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Each section should cover one topic. Long, rambling sections make it harder for
                  the AI to pinpoint the relevant portion.
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-green-100">
                <Icon icon="tabler:check" className="text-sm text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Include key terms</h4>
                <p className="mt-1 text-sm text-gray-600">
                  Use the terminology your users will search for. If users say &quot;cancel&quot;
                  but your document says &quot;terminate,&quot; add both terms.
                </p>
              </div>
            </div>
          </div>

          <h3>File Naming</h3>
          <p>
            Document names appear in citations. Good names help users understand the source at a
            glance.
          </p>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <Icon icon="tabler:x" className="text-red-500" />
                <span className="text-sm font-medium text-red-900">Poor naming</span>
              </div>
              <ul className="space-y-1 text-sm text-red-800">
                <li>doc1.pdf</li>
                <li>final_final_v3.docx</li>
                <li>untitled.txt</li>
                <li>Copy of policies.pdf</li>
              </ul>
            </div>
            <div className="rounded-lg border border-green-200 bg-green-50 p-4">
              <div className="mb-2 flex items-center gap-2">
                <Icon icon="tabler:check" className="text-green-500" />
                <span className="text-sm font-medium text-green-900">Good naming</span>
              </div>
              <ul className="space-y-1 text-sm text-green-800">
                <li>Return-Policy-2024.pdf</li>
                <li>Employee-Benefits-Guide.docx</li>
                <li>API-Authentication-Guide.txt</li>
                <li>Pricing-Enterprise-Plans.pdf</li>
              </ul>
            </div>
          </div>

          <h3>Content Quality</h3>
          <div className="my-6 overflow-x-auto">
            <table className="min-w-full border border-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Do
                  </th>
                  <th className="border-b border-gray-200 px-4 py-3 text-left font-semibold text-gray-900">
                    Don&apos;t
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Write complete, self-contained answers
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Reference other documents (&quot;see policy X&quot;)
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Use text-based PDFs with searchable text
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Upload scanned images without OCR
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Remove duplicate documents
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Keep multiple versions of the same content
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Update outdated content regularly
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Leave obsolete information in the KB
                  </td>
                </tr>
                <tr>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Break large documents into logical sections
                  </td>
                  <td className="border-b border-gray-200 px-4 py-3 text-gray-700">
                    Upload 500-page manuals as single files
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Knowledge Base Hygiene</h3>
          <ul>
            <li>
              <strong>Remove duplicates</strong> — Multiple documents with similar content confuse
              citation attribution
            </li>
            <li>
              <strong>Delete outdated content</strong> — Old policies or deprecated information can
              lead to wrong citations
            </li>
            <li>
              <strong>Consolidate related content</strong> — Better to have one comprehensive
              document than five fragments
            </li>
            <li>
              <strong>Test regularly</strong> — Ask questions and verify citations point to the
              right sources
            </li>
          </ul>
        </section>

        {/* Section 5: Understanding Scores */}
        <section id="understanding-scores" className="scroll-mt-24">
          <h2>5. Understanding Relevance Scores</h2>

          <p>
            Each citation includes a relevance score from 0 to 1, indicating how closely the source
            matched the query.
          </p>

          <h3>Score Interpretation</h3>
          <div className="my-6 space-y-3">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-green-500" />
                  <span className="font-semibold text-gray-900">0.85 - 1.0</span>
                </div>
                <span className="text-sm font-medium text-green-600">Excellent</span>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Strong semantic match. The document directly addresses the question.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-blue-500" />
                  <span className="font-semibold text-gray-900">0.70 - 0.84</span>
                </div>
                <span className="text-sm font-medium text-blue-600">Good</span>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Relevant content that partially answers the question or provides context.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-amber-500" />
                  <span className="font-semibold text-gray-900">0.50 - 0.69</span>
                </div>
                <span className="text-sm font-medium text-amber-600">Moderate</span>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Somewhat related content. May indicate the knowledge base lacks better sources.
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-3 w-3 rounded-full bg-red-500" />
                  <span className="font-semibold text-gray-900">Below 0.50</span>
                </div>
                <span className="text-sm font-medium text-red-600">Weak</span>
              </div>
              <p className="mt-2 text-sm text-gray-600">
                Loosely related or fallback content. Consider adding better source material.
              </p>
            </div>
          </div>

          <h3>Using Scores for Improvement</h3>
          <p>Monitor citation scores to identify knowledge gaps:</p>
          <ul>
            <li>
              <strong>Consistently low scores</strong> — Add more content on that topic
            </li>
            <li>
              <strong>Wrong documents cited</strong> — Improve document titles or restructure
              content
            </li>
            <li>
              <strong>No citations</strong> — The topic isn&apos;t covered in your knowledge base
            </li>
          </ul>
        </section>

        {/* Section 6: Troubleshooting */}
        <section id="troubleshooting" className="scroll-mt-24">
          <h2>6. Troubleshooting Poor Citations</h2>

          <p>Common citation issues and how to fix them.</p>

          <div className="my-6 space-y-4">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">No citations appearing</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>Check that citations are enabled in Appearance settings</li>
                <li>
                  Verify the knowledge base actually has content — check Knowledge Base &rarr;
                  Documents, Links, Text and Q&amp;A
                </li>
                <li>Ensure documents have been processed successfully</li>
                <li>Test with a question you know is covered in your documents</li>
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Wrong documents being cited</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>Remove duplicate or near-duplicate documents</li>
                <li>Use more specific document titles</li>
                <li>Restructure content so each document has a clear focus</li>
                <li>Add the missing correct content if it doesn&apos;t exist</li>
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Low relevance scores</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>Add more content on the topic being asked about</li>
                <li>Use terminology that matches how users ask questions</li>
                <li>Break up large documents into focused sections</li>
                <li>Ensure PDFs have searchable text (not just images)</li>
              </ul>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <h4 className="font-semibold text-gray-900">Citations link doesn&apos;t work</h4>
              <ul className="mt-2 space-y-1 text-sm text-gray-600">
                <li>Document download links expire after 15 minutes—refresh the chat</li>
                <li>Check if the source document was deleted from the knowledge base</li>
                <li>For URLs, verify the original webpage is still accessible</li>
              </ul>
            </div>
          </div>

          <div className="my-8 rounded-xl border border-primary-200 bg-primary-50 p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100">
                <Icon icon="tabler:bulb" className="text-xl text-primary-600" />
              </div>
              <div>
                <h4 className="font-semibold text-primary-900">Pro Tip</h4>
                <p className="mt-1 text-primary-800">
                  Create a test document with known content and test questions. This helps you
                  verify citations are working correctly and gives you a baseline for comparison
                  when troubleshooting other documents.
                </p>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <h3>Next Steps</h3>
          <div className="my-6 grid gap-4 sm:grid-cols-2">
            <Link
              href={PageLink.docsKnowledgeUpload}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:upload"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Knowledge Upload Guide</h4>
              <p className="mt-1 text-sm text-gray-600">
                Best practices for uploading and organizing your knowledge base.
              </p>
            </Link>
            <Link
              href={PageLink.docsTroubleshooting}
              className="group rounded-lg border border-gray-200 bg-white p-6 transition-all hover:border-primary-200 hover:shadow-md">
              <Icon
                icon="tabler:bug"
                className="text-2xl text-gray-400 group-hover:text-primary-500"
              />
              <h4 className="mt-3 font-semibold text-gray-900">Debugging Guide</h4>
              <p className="mt-1 text-sm text-gray-600">Diagnose and fix common chatbot issues.</p>
            </Link>
          </div>
        </section>
      </DocsArticleLayout>
    </>
  );
};

export const getServerSideProps = async () => {
  return { props: {} };
};

export default CitationQualityPage;
